import { Edit } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { PopoverPosition } from "react-tiny-popover";
import { SystemReactTinyPopover } from "../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useEditLocations } from "../MasterSchedule/v2/hooks";
import { LocationSection } from "./LocationSection";

type Props = {
	/** `compact` (32px) in the toolbar beside the other compact controls; `small` standing on its own as a header action. */
	size: "compact" | "small";
	/** Where the editor opens from the button: `bottom` under the toolbar's right-hand group, `left` from the header's edge. */
	popoverPosition: PopoverPosition;
	/** An icon button named by its tooltip, where the toolbar's row has no room for the label. */
	iconOnly: boolean;
};

export const EditUnitFloor: FC<Props> = ({ size, popoverPosition, iconOnly }) => {
	const { t } = useTranslation();
	const { canEditCensusByLocation, isOpen, setIsOpen, openEditor, listsLoaded, units, setUnits, floors, setFloors, isUpdating, handleSubmit } =
		useEditLocations();

	if (!canEditCensusByLocation) return null;

	return (
		<SystemReactTinyPopover
			isOpen={isOpen}
			positions={[popoverPosition]}
			onClose={() => setIsOpen(false)}
			content={
				<Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
					<LocationSection title={t("units")} items={units} setItems={setUnits} />

					<LocationSection title={t("floors")} items={floors} setItems={setFloors} />

					<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
						<SystemButton data-testid="edit-unit-floor-cancel-button" buttonVariant="secondary" onClick={() => setIsOpen(false)} disabled={isUpdating}>
							{t("cancel")}
						</SystemButton>
						<SystemButton data-testid="edit-unit-floor-submit-button" onClick={handleSubmit} disabled={isUpdating}>
							{isUpdating ? t("saving") : t("submit")}
						</SystemButton>
					</Box>
				</Box>
			}
		>
			<SystemButton
				buttonVariant="secondary"
				size={size}
				onClick={() => (isOpen ? setIsOpen(false) : openEditor())}
				disabled={!listsLoaded}
				iconButton={iconOnly}
				leadingIcon={<Edit fontSize="small" />}
				tooltip={iconOnly ? t("edit.locations") : undefined}
				aria-label={t("edit.locations")}
				data-testid="locations-edit-units-floors"
			>
				{iconOnly ? undefined : t("edit.locations")}
			</SystemButton>
		</SystemReactTinyPopover>
	);
};
